/*
 * DM newsletter signup (sections/dm-newsletter.liquid).
 *
 * The form is Shopify's customer form, so it still works without JavaScript.
 * With JavaScript, and when the section has a Klaviyo public key and list ID,
 * the address goes straight to that Klaviyo list through Klaviyo's client
 * subscription API and the page does not reload. It is the same list the
 * Klaviyo form used, so the flows that start from the list keep running.
 *
 * If Klaviyo cannot take the signup (offline, blocked by a privacy
 * extension, timed out, a server or setup error), the form is posted to
 * Shopify as usual, so the signup is never lost. Only an address Klaviyo
 * rejects is shown back to the visitor.
 *
 *   form[data-dm-newsletter]
 *     [data-dm-newsletter-config]   data-company-id, data-list-id, data-source,
 *                                   data-error-invalid
 *     [data-dm-newsletter-fields]   hidden once subscribed
 *     input[type="email"]           the address
 *     [data-dm-newsletter-trap]     spam trap; a filled trap sends nothing
 *     [data-dm-newsletter-submit]   aria-busy while sending
 *     [data-dm-newsletter-error]    message line (role="alert")
 *     [data-dm-newsletter-success]  shown and focused once subscribed
 *
 * After a signup it identifies the visitor to Klaviyo's onsite script, as the
 * Klaviyo form did, pushes `dm_newsletter_signup` to the Google Tag Manager
 * data layer and fires a bubbling `dm:newsletter:subscribed` event, which
 * assets/openai-ads-pixel.js reports as `lead_created`.
 */
(function (root, factory) {
  'use strict';

  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root && root.document && !root.DMNewsletter) {
    root.DMNewsletter = api;
    api.init(root, root.document);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  // Klaviyo supports each API revision for two years; move this date forward
  // before July 2028 (see docs/footer-and-newsletter.md).
  const REVISION = '2026-07-15';
  const TIMEOUT_MS = 12000;
  // One @, no spaces, and a domain with at least one dot and no empty labels.
  const EMAIL = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

  /* ------------------------------ Helpers ------------------------------ */

  function normalizeEmail(value) {
    return String(value == null ? '' : value).trim();
  }

  function isEmail(value) {
    return EMAIL.test(normalizeEmail(value));
  }

  function subscribeUrl(companyId) {
    return `${ENDPOINT}?company_id=${encodeURIComponent(String(companyId).trim())}`;
  }

  // JSON:API body for POST /client/subscriptions (Klaviyo OpenAPI,
  // OnsiteSubscriptionCreateQuery).
  function subscriptionPayload(email, listId, source) {
    const attributes = {
      profile: {
        data: {
          type: 'profile',
          attributes: { email: normalizeEmail(email) },
        },
      },
    };
    const customSource = normalizeEmail(source);
    if (customSource) attributes.custom_source = customSource;

    return {
      data: {
        type: 'subscription',
        attributes,
        relationships: {
          list: { data: { type: 'list', id: String(listId).trim() } },
        },
      },
    };
  }

  // What to do when Klaviyo answers with an error: 'invalid' when it rejected
  // the address itself, otherwise 'fallback' (post the form to Shopify).
  function failureAction(status, body) {
    if (status !== 400 && status !== 422) return 'fallback';
    const errors = body && Array.isArray(body.errors) ? body.errors : [];
    const aboutEmail = errors.some((error) => {
      const pointer = error && error.source && error.source.pointer;
      return typeof pointer === 'string' && /\/email$/.test(pointer);
    });
    return aboutEmail ? 'invalid' : 'fallback';
  }

  /* ------------------------------ Runtime ------------------------------ */

  function init(win, doc) {
    // Settings sit on a hidden element inside the form, because the Liquid
    // form tag takes literal attributes only.
    function configOf(form) {
      const element = form.querySelector('[data-dm-newsletter-config]');
      const data = element ? element.dataset : {};
      return {
        companyId: (data.companyId || '').trim(),
        listId: (data.listId || '').trim(),
        source: (data.source || '').trim(),
        errorInvalid: data.errorInvalid || '',
      };
    }

    const canSend = typeof win.fetch === 'function';
    const isConfigured = (form) => {
      const config = configOf(form);
      return Boolean(config.companyId && config.listId);
    };

    // The script shows its own messages, so the browser's validation bubbles
    // are switched off where it takes over. Without it, they stay.
    function prepare(scope) {
      scope.querySelectorAll('form[data-dm-newsletter]').forEach((form) => {
        if (canSend && isConfigured(form)) form.noValidate = true;
      });
    }

    function parts(form) {
      return {
        input: form.querySelector('input[type="email"]'),
        button: form.querySelector('[data-dm-newsletter-submit]'),
        error: form.querySelector('[data-dm-newsletter-error]'),
        fields: form.querySelector('[data-dm-newsletter-fields]'),
        success: form.querySelector('[data-dm-newsletter-success]'),
        trap: form.querySelector('[data-dm-newsletter-trap]'),
      };
    }

    function showInvalid(form) {
      const { input, error } = parts(form);
      if (!input) return;
      input.setAttribute('aria-invalid', 'true');
      if (error) {
        // An emptied message setting falls back to the browser's own text.
        error.textContent = configOf(form).errorInvalid || input.validationMessage || '';
        error.hidden = !error.textContent;
      }
      input.focus();
    }

    function clearError(form) {
      const { input, error } = parts(form);
      if (input) input.removeAttribute('aria-invalid');
      if (error && !error.hidden) {
        error.hidden = true;
        error.textContent = '';
      }
    }

    function setBusy(button, busy) {
      if (!button) return;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      button.disabled = busy;
    }

    function showSuccess(form) {
      const { fields, success } = parts(form);
      if (fields) fields.hidden = true;
      if (success) {
        success.hidden = false;
        success.focus({ preventScroll: true });
      }
    }

    // Shopify's own post: no submit event fires, so this script stays out of
    // it, and the page reloads showing Shopify's result.
    function postToShopify(form) {
      win.HTMLFormElement.prototype.submit.call(form);
    }

    function announceSignup(form, email) {
      const { source } = configOf(form);
      // Link this browser to the profile for Klaviyo's onsite tracking, as the
      // embedded Klaviyo form does.
      try {
        if (win.klaviyo && typeof win.klaviyo.identify === 'function') {
          win.klaviyo.identify({ email });
        } else if (Array.isArray(win._learnq)) {
          win._learnq.push(['identify', { $email: email }]);
        }
      } catch (error) {
        // Tracking must never break the signup.
      }
      if (Array.isArray(win.dataLayer)) {
        win.dataLayer.push({ event: 'dm_newsletter_signup', newsletter_source: source });
      }
      form.dispatchEvent(new win.CustomEvent('dm:newsletter:subscribed', { bubbles: true, detail: { source } }));
    }

    async function submit(form) {
      const { input, button, trap } = parts(form);
      if (!input || (button && button.disabled)) return;
      const config = configOf(form);

      const email = normalizeEmail(input.value);
      if (!isEmail(email)) {
        showInvalid(form);
        return;
      }
      // Bots fill every field; they get the success screen and nothing is sent.
      if (trap && trap.value) {
        showSuccess(form);
        return;
      }

      clearError(form);
      setBusy(button, true);
      const controller = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      const timer = controller ? win.setTimeout(() => controller.abort(), TIMEOUT_MS) : 0;

      try {
        const response = await win.fetch(subscribeUrl(config.companyId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: REVISION,
          },
          body: JSON.stringify(subscriptionPayload(email, config.listId, config.source)),
          signal: controller ? controller.signal : undefined,
        });
        if (response.ok) {
          setBusy(button, false);
          showSuccess(form);
          announceSignup(form, email);
          return;
        }
        let body = null;
        try {
          body = await response.json();
        } catch (error) {
          // An error without a JSON body is handled like any other failure.
        }
        if (failureAction(response.status, body) === 'invalid') {
          setBusy(button, false);
          showInvalid(form);
          return;
        }
        if (win.console) win.console.warn(`Newsletter: Klaviyo answered ${response.status}; posting the signup to Shopify instead.`);
        postToShopify(form);
      } catch (error) {
        postToShopify(form);
      } finally {
        if (timer) win.clearTimeout(timer);
      }
    }

    // Capture phase on the window, ahead of other submit handlers (Shopify's
    // spam protection, theme scripts): when Klaviyo takes the signup, nothing
    // else should act on this submit. Without Klaviyo set up, or without
    // fetch, Shopify's customer form posts as usual.
    win.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof win.HTMLFormElement) || !form.hasAttribute('data-dm-newsletter')) return;
        if (!canSend || !isConfigured(form)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        submit(form);
      },
      true
    );

    // Typing a new address clears the last error.
    doc.addEventListener('input', (event) => {
      const input = event.target;
      if (!(input instanceof win.HTMLInputElement) || input.getAttribute('aria-invalid') !== 'true') return;
      const form = input.closest('form[data-dm-newsletter]');
      if (form) clearError(form);
    });

    // Back from the Shopify fallback via the browser cache: re-enable buttons.
    win.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      doc.querySelectorAll('form[data-dm-newsletter] [data-dm-newsletter-submit]').forEach((button) => setBusy(button, false));
    });

    doc.addEventListener('shopify:section:load', (event) => prepare(event.target));

    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', () => prepare(doc));
    } else {
      prepare(doc);
    }

    return { prepare, submit };
  }

  return {
    REVISION,
    failureAction,
    init,
    isEmail,
    normalizeEmail,
    subscribeUrl,
    subscriptionPayload,
  };
});
